// U.28 — okna dat, o które ekrany pytają GET /api/all-reservations i GET /api/rentals.
//
// Do U.28 kalendarz, strona główna i kalendarz sztuki pobierały listę BEZ parametrów, a serwer
// oddawał wtedy 200 NAJSTARSZYCH rezerwacji. Firma z większą liczbą rezerwacji przestawała
// widzieć nowe. Teraz każdy ekran pyta o okno, które faktycznie pokazuje; serwer zwraca
// rezerwacje NACHODZĄCE na okno (także zaczęte przed nim), więc wystarczy podać zakres dni.
//
// Czysty moduł: bez Reacta i bez sieci. Daty jako `RRRR-MM-DD` liczone LOKALNIE (formatYMD),
// nigdy przez toISOString — ta cofa dzień między północą a 2:00 czasu polskiego.
import { formatYMD } from './contract';

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/**
 * Okno widocznej siatki miesiąca w react-calendar. Siatka pokazuje pełne tygodnie, więc ma też
 * dni sąsiednich miesięcy: zaczyna się najwyżej 6 dni przed 1. dniem (tydzień od poniedziałku
 * albo od niedzieli, zależnie od języka) i ma najwyżej 6 tygodni, czyli kończy się najpóźniej
 * 41 dni po 1. dniu. Bierzemy ten największy zakres, żeby nie zależeć od języka ani od tego,
 * ile tygodni akurat ma miesiąc — kilka dni zapasu kosztuje kilka wierszy.
 */
export function monthGridWindow(anyDayOfMonth) {
  const d = anyDayOfMonth instanceof Date ? anyDayOfMonth : new Date(anyDayOfMonth);
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return { from: formatYMD(addDays(first, -6)), to: formatYMD(addDays(first, 41)) };
}

/** Okno od dnia `start` przez `days` kolejnych dni (0 = sam ten dzień). */
export function daysWindow(start, days = 0) {
  const s = startOfDay(start instanceof Date ? start : new Date(start));
  return { from: formatYMD(s), to: formatYMD(addDays(s, days)) };
}

const parseDay = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Okna, o które ekran pyta, scalone TYLKO wtedy, gdy nachodzą na siebie albo się stykają
 * (puste pozycje pomijane, wynik posortowany od najwcześniejszego).
 *
 * Nie scalamy okien odległych: jedno okno „od wybranego dnia do oglądanego miesiąca" obejmowało
 * po kilku kliknięciach strzałki cały kwartał, a serwer sortuje rosnąco i tnie limitem, więc
 * w dużej firmie odpadał właśnie oglądany miesiąc (koniec okna). Osobne krótkie okna tego nie
 * mają; wyniki łączy `mergeReservationLists`. Porównanie tekstowe działa, bo `RRRR-MM-DD`
 * sortuje się jak daty.
 */
export function mergeWindows(windows) {
  const list = windows
    .filter((w) => w && w.from && w.to)
    .map((w) => ({ from: w.from, to: w.to }))
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  const merged = [];
  list.forEach((w) => {
    const last = merged[merged.length - 1];
    if (last && w.from <= formatYMD(addDays(parseDay(last.to), 1))) {
      if (w.to > last.to) last.to = w.to;
    } else {
      merged.push(w);
    }
  });
  return merged;
}

/** Stały klucz zestawu okien — do zależności efektów i do sprawdzania, co już wczytano. */
export const windowsKey = (windows) => windows.map((w) => `${w.from}|${w.to}`).join(',');

/** Listy rezerwacji z kilku okien (albo zakładek) jako jedna, bez powtórzeń, w kolejności pierwszego wystąpienia. */
export function mergeReservationLists(...lists) {
  const seen = new Map();
  lists.flat().forEach((r) => { if (r && !seen.has(r.id)) seen.set(r.id, r); });
  return [...seen.values()];
}

/**
 * Tekst zapytania dla trasy listy. `window` może być `null` (np. historia bez filtra dat),
 * a puste wartości w `extra` są pomijane, żeby serwer dostał wyłącznie ustawione parametry.
 */
export function reservationListQuery(window, extra = {}) {
  const params = new URLSearchParams();
  if (window?.from) params.set('from', window.from);
  if (window?.to) params.set('to', window.to);
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const text = params.toString();
  return text ? `?${text}` : '';
}
